import { formatEur } from '@/lib/cashFlow';
import { cn } from '@/lib/utils';

/** A SIGNED cash amount, tinted by the value finance-core already computed: a positive
 *  (cash IN) figure reads income-green, a negative (cash OUT) figure reads cost-red with
 *  the locale minus, zero is muted. The cents come PRE-SIGNED from `signedSumEntries` /
 *  `signedOutstandingOf` / `signedPaidAmountOf` (income +, cost −; a credit note is stored
 *  negative, so its direction sign already reads cash-out/in), so a per-row amount, the
 *  register footer "Kokku" and the settlement hover can never disagree. The euro string is
 *  `formatEur` (a pure formatter — the locale minus for negatives); the component never
 *  computes or negates the number itself (L9 — no money math in components). Shared by the
 *  register table and the settlement detail so the signed presentation lives in ONE atom. */
export function SignedEur({ cents }: { cents: number }) {
  return (
    <span
      className={cn(
        'tabular-nums font-semibold',
        cents > 0
          ? 'text-[hsl(var(--income))]'
          : cents < 0
            ? 'text-[hsl(var(--cost))]'
            : 'text-muted-foreground',
      )}
    >
      {formatEur(cents)}
    </span>
  );
}

/** A NEUTRAL signed cash amount for the register's per-row money columns: the figure is
 *  colour-neutral (so an all-income / all-cost column is no longer a wall of green/red —
 *  the readability fix), the cash DIRECTION reads from an explicit leading sign (income
 *  `+`, cost `−`; zero unsigned). `tone` emphasises the active money mode (`strong`) and
 *  dims KM data (`faint`). The cents arrive PRE-SIGNED from `signedSumEntries` (income +,
 *  cost −), so this never computes or negates (L9 — `formatEur` already renders the locale
 *  minus; only the explicit `+` for positives is added here). Net direction colour stays
 *  on the SIGNED aggregates (`SignedEur`: footer / subtotal / selection totals). */
export function PlainAmount({
  cents,
  tone = 'normal',
}: {
  cents: number;
  tone?: 'strong' | 'normal' | 'faint';
}) {
  const toneCls =
    tone === 'strong'
      ? 'font-semibold text-foreground'
      : tone === 'faint'
        ? 'text-muted-foreground/55'
        : 'text-foreground/80';
  return (
    <span className={cn('tabular-nums', toneCls)}>
      {cents > 0 ? '+' : ''}
      {formatEur(cents)}
    </span>
  );
}
